import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { semanticTone } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import {
  attachBuiltin,
  attachTool,
  detachBuiltin,
  detachTool,
  findAttachment,
  isBuiltinAttached,
  isToolAttached,
  sanitizeAttachmentsForApi,
  setAttachmentUsage,
} from 'components/@home/@settings/Tabs/Agents/attachedToolsHelpers';
import CloseIcon from 'mdi-react/CloseIcon';
import CodeTagsIcon from 'mdi-react/CodeTagsIcon';
import LightningBoltIcon from 'mdi-react/LightningBoltIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import WebhookIcon from 'mdi-react/WebhookIcon';
import WrenchOutlineIcon from 'mdi-react/WrenchOutlineIcon';
import { normalizeToolTrigger } from 'constants/agenticToolTriggers';
import { arrayOf, bool, func, object, string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import Agent from 'services/api/Agent';
import { I18n } from 'utils/i18n';

const getStyles = theme => {
  const mutedChip = semanticTone(theme, 'default');
  const warningTone = semanticTone(theme, 'warning');
  return {
    paper: { borderRadius: '16px' },
    title: {
      fontSize: theme.typography.pxToRem(18),
      fontWeight: 700,
      padding: theme.spacing(2.5, 3, 1.5, 3),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    content: {
      padding: theme.spacing(0, 3, 0, 3),
      '&.MuiDialogContent-root': { paddingTop: 0 },
    },
    toolbar: {
      display: 'flex',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
    },
    search: {
      flex: 1,
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: theme.palette.background.default,
      },
    },
    sectionLabel: {
      fontSize: theme.typography.pxToRem(11),
      fontWeight: 700,
      letterSpacing: '0.6px',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(0.5),
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    scroller: {
      maxHeight: 460,
      overflowY: 'auto',
      paddingRight: theme.spacing(0.5),
    },
    row: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '12px',
      backgroundColor: theme.palette.background.paper,
    },
    rowHead: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
    iconWrap: {
      width: 36,
      height: 36,
      borderRadius: '8px',
      backgroundColor: theme.palette.background.default,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.palette.text.secondary,
      flexShrink: 0,
    },
    name: {
      fontSize: theme.typography.pxToRem(14),
      fontWeight: 600,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    meta: {
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    kindChip: {
      height: 22,
      fontSize: theme.typography.pxToRem(11),
      backgroundColor: mutedChip.bg,
      color: mutedChip.fg,
      '& .MuiChip-label': { padding: '0 8px' },
    },
    builtinChip: {
      backgroundColor: warningTone.bg,
      color: warningTone.fg,
    },
    emptyState: {
      padding: theme.spacing(2),
      textAlign: 'center',
      color: theme.palette.text.secondary,
      fontSize: theme.typography.pxToRem(13),
    },
    actions: {
      padding: theme.spacing(2, 3),
      gap: theme.spacing(1),
    },
    usageWrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
    usageHint: {
      fontSize: theme.typography.pxToRem(11.5),
      color: theme.palette.text.secondary,
    },
  };
};

function renderItemIcon(item) {
  if (item.kind === 'builtin') return <LightningBoltIcon size={16} />;
  if (item.toolKind === 'http') return <WebhookIcon size={16} />;
  if (item.toolKind === 'code') return <CodeTagsIcon size={16} />;
  return <WrenchOutlineIcon size={16} />;
}

function renderItemMeta(item) {
  if (item.kind === 'builtin') {
    return I18n.t('AgenticChatFlow.BuiltinTool');
  }
  const parts = [];
  if (item.trigger) {
    const key = normalizeToolTrigger(item.trigger);
    parts.push(I18n.t(`AgenticChatFlow.Trigger_${key}`, { defaultValue: key }));
  }
  if (item.toolKind === 'http') parts.push(I18n.t('AgenticChatFlow.KindHttp'));
  else if (item.toolKind === 'code') parts.push(I18n.t('AgenticChatFlow.KindCode'));
  if (item.description) parts.push(item.description);
  return parts.join(' · ') || null;
}

const AttachToolsDialog = ({
  open,
  onClose,
  tools,
  selectedAttachments = [],
  onSave,
  onCreateTool = null,
  dialogTitle = null,
  catalogBuiltinFilter = null,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [search, setSearch] = useState('');
  const [local, setLocal] = useState([]);
  const [builtinTools, setBuiltinTools] = useState([]);

  useEffect(() => {
    Agent.listBuiltinTools()
      .then(list => setBuiltinTools(Array.isArray(list) ? list : []))
      .catch(() => setBuiltinTools([]));
  }, []);

  useEffect(() => {
    if (open) {
      setLocal(JSON.parse(JSON.stringify(selectedAttachments || [])));
      setSearch('');
    }
  }, [open, selectedAttachments]);

  const items = useMemo(() => {
    const builtins = catalogBuiltinFilter
      ? builtinTools.filter(catalogBuiltinFilter)
      : builtinTools;
    const builtinItems = builtins.map(b => ({
      _id: `builtin:${b.name}`,
      kind: 'builtin',
      builtinName: b.name,
      name: b.name,
      description: b.description,
    }));
    const toolItems = tools.map(t => ({
      _id: `tool:${t._id}`,
      kind: 'tool',
      toolId: t._id,
      toolKind: t.kind,
      trigger: t.trigger,
      name: t.displayName || t.name,
      slug: t.name,
      description: t.description,
    }));
    const sorted = (arr, getKey) =>
      arr.slice().sort((a, b) => (getKey(a) || '').localeCompare(getKey(b) || ''));
    return [...sorted(builtinItems, i => i.name), ...sorted(toolItems, i => i.name)];
  }, [tools, builtinTools, catalogBuiltinFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i => `${i.name} ${i.description || ''}`.toLowerCase().includes(q));
  }, [items, search]);

  const builtinFiltered = filtered.filter(i => i.kind === 'builtin');
  const toolFiltered = filtered.filter(i => i.kind === 'tool');

  const isAttached = item =>
    item.kind === 'builtin'
      ? isBuiltinAttached(local, item.builtinName)
      : isToolAttached(local, item.toolId);

  const usageFor = item => {
    const att =
      item.kind === 'builtin'
        ? findAttachment(local, { builtin: item.builtinName })
        : findAttachment(local, { tool: item.toolId });
    return att?.usage || '';
  };

  const toggle = item => {
    if (item.kind === 'builtin') {
      if (isBuiltinAttached(local, item.builtinName)) {
        setLocal(detachBuiltin(local, item.builtinName));
      } else {
        setLocal(attachBuiltin(local, item.builtinName));
      }
    } else if (isToolAttached(local, item.toolId)) {
      setLocal(detachTool(local, item.toolId));
    } else {
      setLocal(attachTool(local, item.toolId));
    }
  };

  const updateUsage = (item, usage) => {
    setLocal(
      setAttachmentUsage(
        local,
        item.kind === 'builtin' ? { builtin: item.builtinName } : { tool: item.toolId },
        usage,
      ),
    );
  };

  const dirty = useMemo(() => {
    const norm = a => JSON.stringify(sanitizeAttachmentsForApi(a));
    return norm(local) !== norm(selectedAttachments);
  }, [local, selectedAttachments]);

  const handleSave = () => {
    onSave(sanitizeAttachmentsForApi(local));
    onClose();
  };

  const renderRow = item => {
    const attached = isAttached(item);
    const isSendMessage = item.kind === 'builtin' && item.builtinName === 'send_message';
    return (
      <Box key={item._id} sx={styles.row}>
        <Box sx={styles.rowHead}>
          <Box component="span" sx={styles.iconWrap}>
            {renderItemIcon(item)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={styles.name} noWrap>
                {item.name}
              </Typography>
              {item.kind === 'builtin' && (
                <Chip
                  size="small"
                  label={I18n.t('AgenticChatFlow.BuiltinTool')}
                  sx={[styles.kindChip, styles.builtinChip]}
                />
              )}
            </Box>
            {renderItemMeta(item) && (
              <Typography sx={styles.meta}>{renderItemMeta(item)}</Typography>
            )}
          </Box>
          {attached ? (
            <Button variant="outlined" size="small" color="secondary" onClick={() => toggle(item)}>
              {I18n.t('AgenticChatFlow.Detach')}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              disableElevation
              color="primary"
              onClick={() => toggle(item)}
            >
              {I18n.t('AgenticChatFlow.Attach')}
            </Button>
          )}
        </Box>
        {attached && (
          <Box sx={styles.usageWrap}>
            <TextField
              value={usageFor(item)}
              onChange={e => updateUsage(item, e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder={
                isSendMessage
                  ? I18n.t('AgenticChatFlow.UsagePlaceholderSendMessage')
                  : I18n.t('AgenticChatFlow.UsagePlaceholder')
              }
            />
            <Typography sx={styles.usageHint}>
              {isSendMessage
                ? I18n.t('AgenticChatFlow.UsageHintSendMessage')
                : I18n.t('AgenticChatFlow.UsageHint')}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: styles.paper } }}
    >
      <DialogTitle sx={styles.title} disableTypography>
        <Typography component="span" sx={{ fontSize: 18, fontWeight: 700 }}>
          {dialogTitle || I18n.t('AgenticChatFlow.AttachToolsTitle')}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label={I18n.t('Common.close')}>
          <CloseIcon size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <Box sx={styles.toolbar}>
          <TextField
            sx={styles.search}
            size="small"
            placeholder={I18n.t('AgenticChatFlow.SearchTools')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <MagnifyIcon size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />
          {onCreateTool && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<PlusIcon size={16} />}
              onClick={() => {
                onClose();
                onCreateTool();
              }}
            >
              {I18n.t('AgenticChatFlow.CreateTool')}
            </Button>
          )}
        </Box>
        <Box sx={styles.scroller}>
          {builtinFiltered.length > 0 && (
            <>
              <Typography sx={styles.sectionLabel}>
                {I18n.t('AgenticChatFlow.BuiltinTools')}
              </Typography>
              <Box sx={styles.list}>{builtinFiltered.map(renderRow)}</Box>
            </>
          )}
          {toolFiltered.length > 0 && (
            <>
              <Typography sx={styles.sectionLabel}>
                {I18n.t('AgenticChatFlow.LibraryTools')}
              </Typography>
              <Box sx={styles.list}>{toolFiltered.map(renderRow)}</Box>
            </>
          )}
          {filtered.length === 0 && (
            <Box sx={styles.emptyState}>
              {items.length === 0
                ? I18n.t('AgenticChatFlow.NoToolsLibrary')
                : I18n.t('AgenticChatFlow.NoToolsMatchSearch')}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button variant="text" color="secondary" onClick={onClose}>
          {I18n.t('Common.Cancel')}
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={!dirty}>
          {I18n.t('AgenticChatFlow.Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AttachToolsDialog.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  tools: arrayOf(object).isRequired,
  selectedAttachments: arrayOf(object),
  onSave: func.isRequired,
  onCreateTool: func,
  dialogTitle: string,
  catalogBuiltinFilter: func,
};

export default AttachToolsDialog;
